import {
  BlockType,
  DiscountApplicationTypeNotEmpty,
} from "@intelligems/ig-types";
import {
  moduleIdForOfferType,
  SMART_CHECKOUT_ID,
  SMART_MERCHANDISING_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_SHIPPING_ID,
} from "@src/constants/billing";

export type CategoryKey =
  | "content"
  | "merchandising"
  | "pricing"
  | "shipping"
  | "checkout"
  | "postPurchase";

export type CategoryFilter = CategoryKey | "all";

export type CreateExperimentDialogRenderLocation =
  | CategoryKey
  | "homeDashboard"
  | "tests"
  | "getInspired"
  | "get-started";

export type GraphicKind =
  | "contentEdits"
  | "redirects"
  | "stylesAndJavaScript"
  | "theme"
  | "template"
  | "productDiscount"
  | "orderDiscount"
  | "volumeDiscount"
  | "freeGift"
  | "priceChange"
  | "shippingRates"
  | "freeShipping"
  | "upsell"
  | "trustBadge"
  | "shippingProgress"
  | "callout"
  | "staticImage"
  | "brandLogo"
  | "countdownTimer"
  | "textOnly"
  | "postPurchaseUpsell"
  | "postPurchaseFunnel"
  | "onsiteUpsell"
  | "onsiteBundleUpsell"
  | "buyXGetY";

export type OfferPreset = {
  discountType: DiscountApplicationTypeNotEmpty;
  discountMethod?: "perItem" | "perOrder";
  unitType?: "unit" | "dollar";
};

export type PersonalizationAction =
  | {
      kind: "mod";
      modToActivate:
        | "onsiteEdits"
        | "splitUrl"
        | "onsiteInjections"
        | "theme"
        | "template"
        | "price"
        | "onsiteUpsell";
    }
  | { kind: "offer"; preset: OfferPreset }
  | {
      kind: "checkoutBlock";
      checkoutBlockType: BlockType;
      checkoutBlockTemplate?: string;
    }
  | { kind: "postPurchase"; postPurchaseType: string };

export type TestAction = {
  key:
    | "onsite_test"
    | "split_test"
    | "advanced_test"
    | "template_test"
    | "theme_test"
    | "volume_discount_experiment"
    | "pricing_experiment"
    | "shipping_experiment"
    | "checkout_block_experiment"
    | "post_purchase_experiment"
    | "onsite_upsell_experiment";
  experienceType: string;
  mpProductCategory: string;
  moduleId?: string;
  state?: Record<string, unknown>;
};

export type ExperienceCreationCard = {
  id: string;
  title: string;
  description: string;
  category: CategoryKey;
  graphic: GraphicKind;
  filterKeys: TestAction["key"][];
  isAiPowered?: boolean;
  test?: TestAction;
  personalization?: PersonalizationAction;
  disabledReason?: string;
  opensRequestAccessForm?: boolean;
  badgeContent?: string;
};

export const categoryLabels: Record<CategoryFilter, string> = {
  all: "All",
  content: "Content",
  merchandising: "Merchandising",
  pricing: "Pricing",
  shipping: "Shipping",
  checkout: "Checkout",
  postPurchase: "Post-purchase",
};

export const categoryOrder: CategoryKey[] = [
  "content",
  "merchandising",
  "pricing",
  "shipping",
  "checkout",
  "postPurchase",
];

export const categoryFilterOrder: CategoryFilter[] = ["all", ...categoryOrder];

export const offerTest = (preset: OfferPreset): TestAction => ({
  key: "volume_discount_experiment",
  experienceType: "personalization/offer",
  mpProductCategory: "offer_test",
  moduleId: moduleIdForOfferType(preset.discountType),
  state: preset,
});

export const checkoutTest = (state?: Record<string, unknown>): TestAction => ({
  key: "checkout_block_experiment",
  experienceType: "checkoutBlock",
  mpProductCategory: "checkout_block_test",
  moduleId: SMART_CHECKOUT_ID,
  state,
});

export const onsiteUpsellTest: TestAction = {
  key: "onsite_upsell_experiment",
  experienceType: "onsiteUpsell",
  mpProductCategory: "onsite_upsell_test",
  moduleId: SMART_MERCHANDISING_ID,
};

export const pricingTest: TestAction = {
  key: "pricing_experiment",
  experienceType: "pricing",
  mpProductCategory: "price_test",
  moduleId: SMART_PRICING_LIMITED_ID,
};

export const shippingTest: TestAction = {
  key: "shipping_experiment",
  experienceType: "shipping",
  mpProductCategory: "shipping_test",
  moduleId: SMART_SHIPPING_ID,
};

export const postPurchaseTest = (
  state?: Record<string, unknown>
): TestAction => ({
  key: "post_purchase_experiment",
  experienceType: "postPurchase",
  mpProductCategory: "post_purchase_test",
  moduleId: SMART_CHECKOUT_ID,
  state,
});
